/**
 * PRTL-1512: the one home for time-zone maths. Every conversion between an instant and a
 * wall clock in some zone goes through `Intl.DateTimeFormat#formatToParts` here; nothing
 * else in the repo calls dayjs's `.tz()` (lint: `compass/no-dayjs-tz`).
 *
 * Why not `dayjs.tz`: a `.tz()` value keeps the UTC offset it was created with, so
 * `add` / `startOf` / `endOf` across a DST change land an hour off (PRTL-1503, PRTL-1501,
 * the admin fall-back hour in #1239); and `dayjs.tz(text, zone)` builds the zone's wall
 * clock as a Date in the PROCESS's zone, so a wall time inside the process's own
 * spring-forward gap comes out an hour late. Intl reads the zone's fields directly,
 * whatever zone the browser or server runs in.
 *
 * Wall-clock times that don't exist or happen twice: a time in the spring-forward gap
 * (02:30 on the second Sunday of March in US zones) resolves an hour later (03:30 DST); a
 * time in the repeated fall-back hour (01:30 on the first Sunday of November) resolves to
 * its first, daylight-time instance.
 *
 * Plain dayjs (no zone) is still fine for date-only work, and `wallDayjs` hands back a
 * dayjs in UTC mode whose fields ARE the wall clock: formatting and calendar steps on it
 * can't be skewed by any DST change, and `fromWallDayjs` reads it back as an instant.
 */
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// ---- local calendar arithmetic -----------------------------------------------

/** A wall-clock date and time in some zone. Months are 1-based. */
export interface WallClock {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
}

type LocalDateTime = WallClock;

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

// Every zone conversion goes through Intl, never dayjs's `.tz()`: that builds
// a Date from the target zone's wall-clock text in THIS process's zone, so a
// time that falls in the process's own spring-forward gap came out an hour off
// (a London browser on 2026-03-29, PRTL-1500 follow-up). Intl reads the zone's
// fields directly, whatever zone the browser or server is in.
const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function zonedParts(ms: number, timeZone: string): Record<string, string> {
    let formatter = partsFormatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            weekday: "short",
            timeZoneName: "short",
        });
        partsFormatters.set(timeZone, formatter);
    }
    const out: Record<string, string> = {};
    for (const part of formatter.formatToParts(new Date(ms))) out[part.type] = part.value;
    return out;
}

function toLocal(date: Date, timeZone: string): LocalDateTime {
    const ms = date.getTime();
    const p = zonedParts(ms, timeZone);
    return {
        year: Number(p.year),
        month: Number(p.month),
        day: Number(p.day),
        hour: Number(p.hour),
        minute: Number(p.minute),
        second: Number(p.second),
        millisecond: ((ms % 1000) + 1000) % 1000,
    };
}

/** The zone's UTC offset in minutes at an instant (e.g. -240 for EDT). */
function offsetAt(ms: number, timeZone: string): number {
    const l = toLocal(new Date(ms), timeZone);
    const wall = Date.UTC(l.year, l.month - 1, l.day, l.hour, l.minute, l.second, l.millisecond);
    return Math.round((wall - ms) / 60_000);
}

function sameWallClock(ms: number, local: LocalDateTime, timeZone: string): boolean {
    const got = toLocal(new Date(ms), timeZone);
    return (
        got.year === local.year &&
        got.month === local.month &&
        got.day === local.day &&
        got.hour === local.hour &&
        got.minute === local.minute &&
        got.second === local.second
    );
}

/**
 * The instant a wall-clock time means in `timeZone`. Worked out from the
 * zone's offsets around that date, never from today's: `dayjs.tz(text, zone)`
 * seeds its guess with the current offset, so the repeated fall-back hour
 * resolved to EDT in summer and to EST in winter (PRTL-1500 review). Of the
 * offsets in force a day either side, keep each that maps back to the same
 * wall clock and take the earliest instant: the repeated hour's first (DST)
 * instance. A time in the spring-forward gap matches neither, so it is read
 * with the offset from before the change, which lands an hour later
 * (02:30 → 03:30 EDT).
 */
function fromLocal(local: LocalDateTime, timeZone: string): Date {
    const wall = Date.UTC(
        local.year,
        local.month - 1,
        local.day,
        local.hour,
        local.minute,
        local.second,
        local.millisecond,
    );
    const day = 86_400_000;
    const before = offsetAt(wall - day, timeZone);
    const after = offsetAt(wall + day, timeZone);
    const candidates = [...new Set([before, after])]
        .map((offset) => wall - offset * 60_000)
        .filter((ms) => sameWallClock(ms, local, timeZone))
        .sort((a, b) => a - b);
    return new Date(candidates[0] ?? wall - before * 60_000);
}

// Date.UTC is used purely as a proleptic calendar here — no zone is involved,
// so adding days can never be skewed by a DST change.
function civil(year: number, month: number, day: number): Date {
    return new Date(Date.UTC(year, month - 1, day));
}

function addLocalDaysTo(local: LocalDateTime, days: number): LocalDateTime {
    const d = civil(local.year, local.month, local.day + days);
    return { ...local, year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function daysInMonth(year: number, month: number): number {
    return civil(year, month + 1, 0).getUTCDate();
}

/** Same day of month `months` later, clamped to the end of a shorter month (Jan 31 → Feb 28). */
function addLocalMonthsTo(local: LocalDateTime, months: number): LocalDateTime {
    const index = local.year * 12 + (local.month - 1) + months;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    return { ...local, year, month, day: Math.min(local.day, daysInMonth(year, month)) };
}

// ---- wall-clock helpers for the admin forms ---------------------------------
// The admin enters and reviews a series' dates in the series' zone, not the
// browser's, so a 10:00 Pacific series reads 10:00 for an admin in Chicago.

/** "YYYY-MM-DDTHH:mm" in `timeZone`, for <input type="datetime-local">. */
export function toZonedDateTimeInput(date: Date, timeZone: string): string {
    const l = toLocal(date, timeZone);
    return `${pad(l.year, 4)}-${pad(l.month)}-${pad(l.day)}T${pad(l.hour)}:${pad(l.minute)}`;
}

/** "YYYY-MM-DD" in `timeZone`, for <input type="date">. */
export function toZonedDateInput(date: Date, timeZone: string): string {
    const l = toLocal(date, timeZone);
    return `${pad(l.year, 4)}-${pad(l.month)}-${pad(l.day)}`;
}

const DATE_TIME_INPUT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
const DATE_INPUT = /^(\d{4})-(\d{2})-(\d{2})$/;

function validLocal(local: LocalDateTime): boolean {
    return (
        local.month >= 1 &&
        local.month <= 12 &&
        local.day >= 1 &&
        local.day <= daysInMonth(local.year, local.month) &&
        local.hour <= 23 &&
        local.minute <= 59 &&
        local.second <= 59
    );
}

/**
 * The instant a wall-clock "YYYY-MM-DDTHH:mm" (or "YYYY-MM-DD HH:mm[:ss]")
 * means in `timeZone`; null when it isn't a complete, real date and time.
 */
export function fromZonedDateTimeInput(value: string, timeZone: string): Date | null {
    const m = DATE_TIME_INPUT.exec(value.trim());
    if (!m) return null;
    const local: LocalDateTime = {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: Number(m[4]),
        minute: Number(m[5]),
        second: m[6] ? Number(m[6]) : 0,
        millisecond: 0,
    };
    return validLocal(local) ? fromLocal(local, timeZone) : null;
}

const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2}|\bGMT|\bUTC)$/i;

/**
 * A start typed or imported as text, read in `timeZone`: a wall-clock
 * "YYYY-MM-DD HH:mm" (or with a T, or a bare date meaning midnight) is that
 * time in the zone; a value with an explicit offset or Z is that instant;
 * any other format `Date` understands ("10/5/2026 10:00 AM") is read for its
 * wall-clock fields, which are then placed in the zone. Null if unparseable.
 */
export function parseZonedDateTime(value: string, timeZone: string): Date | null {
    const text = value.trim();
    if (!text) return null;
    if (DATE_INPUT.test(text)) return startOfZonedDay(text, timeZone);
    if (DATE_TIME_INPUT.test(text)) return fromZonedDateTimeInput(text, timeZone);
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;
    if (EXPLICIT_OFFSET.test(text)) return parsed;
    // Parsed in this process's zone; its local fields are what was written.
    return fromLocal(
        {
            year: parsed.getFullYear(),
            month: parsed.getMonth() + 1,
            day: parsed.getDate(),
            hour: parsed.getHours(),
            minute: parsed.getMinutes(),
            second: parsed.getSeconds(),
            millisecond: 0,
        },
        timeZone,
    );
}

/** A Date's wall-clock fields in this process's zone as "YYYY-MM-DDTHH:mm:ss" (spreadsheet date cells). */
export function localWallClock(date: Date): string {
    return (
        `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
}

/** Midnight at the start of a "YYYY-MM-DD" date in `timeZone`; null if not a real date. */
export function startOfZonedDay(value: string, timeZone: string): Date | null {
    const m = DATE_INPUT.exec(value.trim());
    if (!m) return null;
    return fromZonedDateTimeInput(`${m[1]}-${m[2]}-${m[3]}T00:00`, timeZone);
}

/** The last millisecond of a "YYYY-MM-DD" date in `timeZone`; null if not a real date. */
export function endOfZonedDay(value: string, timeZone: string): Date | null {
    const start = startOfZonedDay(value, timeZone);
    if (!start) return null;
    const [year, month, day] = value.trim().split("-").map(Number);
    const next = addLocalDaysTo(
        { year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 },
        1,
    );
    return new Date(fromLocal(next, timeZone).getTime() - 1);
}

/** The same wall-clock time `days` local days later, in `timeZone` (DST-safe). */
export function addZonedDays(date: Date, days: number, timeZone: string): Date {
    if (days === 0) return new Date(date.getTime());
    return fromLocal(addLocalDaysTo(toLocal(date, timeZone), days), timeZone);
}

/** The same wall-clock time and day of month `months` later, clamped, in `timeZone`. */
export function addZonedMonths(date: Date, months: number, timeZone: string): Date {
    if (months === 0) return new Date(date.getTime());
    return fromLocal(addLocalMonthsTo(toLocal(date, timeZone), months), timeZone);
}

/** The zone's short name at `date` ("EDT", "PST"), for labelling times in that zone. */
export function zoneAbbreviation(date: Date, timeZone: string): string {
    return zonedParts(date.getTime(), timeZone).timeZoneName ?? timeZone;
}

/** "Mon", "Tue", … for the local date of `date` in `timeZone`. */
export function zonedWeekdayShort(date: Date, timeZone: string): string {
    return zonedParts(date.getTime(), timeZone).weekday;
}

/** "Sun Oct 11 8:00 PM EDT" — an instant as a person in `timeZone` reads it (logs, reports). */
export function formatInZone(date: Date, timeZone: string): string {
    const p = zonedParts(date.getTime(), timeZone);
    const hour = Number(p.hour);
    const month = new Intl.DateTimeFormat("en-US", { timeZone, month: "short" }).format(date);
    const clock = `${hour % 12 || 12}:${p.minute} ${hour < 12 ? "AM" : "PM"}`;
    return `${p.weekday} ${month} ${Number(p.day)} ${clock} ${p.timeZoneName ?? timeZone}`;
}

// ---- floating wall clock for a grid that runs in the runtime's zone ---------
// PRTL-1511: react-big-calendar lays days out in the browser's zone, and its
// localizer must keep doing so (PRTL-1503: stepping days through `dayjs.tz`
// skews across DST changes). To show the grid in the profile's zone instead,
// each instant is handed to the grid as a "floating" Date whose LOCAL fields
// are the instant's wall clock in that zone, and every Date the grid hands back
// (a clicked day, the visible month) is read back the same way.

/**
 * A Date whose fields in this runtime's zone (`getFullYear()`, `getHours()`, …)
 * are `date`'s wall clock in `timeZone`. For display on a local-time grid only:
 * it is not the same instant unless the two zones agree. A wall clock inside the
 * runtime zone's own spring-forward gap can't be represented and shows an hour
 * later.
 */
export function toFloatingLocal(date: Date, timeZone: string): Date {
    const l = toLocal(date, timeZone);
    const out = new Date(0);
    out.setFullYear(l.year, l.month - 1, l.day);
    out.setHours(l.hour, l.minute, l.second, l.millisecond);
    return out;
}

/**
 * The instant meant by a floating Date's local wall clock, read in `timeZone`:
 * the inverse of `toFloatingLocal`. A gap time resolves an hour later and a
 * repeated time to its first instance, as `fromZonedDateTimeInput` does.
 */
export function fromFloatingLocal(date: Date, timeZone: string): Date {
    return fromLocal(
        {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
            millisecond: date.getMilliseconds(),
        },
        timeZone,
    );
}

// ---- instants and wall clocks (PRTL-1512) ------------------------------------

/**
 * An instant: a Date, epoch ms, or a string `new Date()` reads. A string should carry its
 * offset ("…Z", "…-04:00"): an offset-less date-time ("2026-11-01T01:30") is read in the
 * PROCESS's zone by `Date` (a bare "YYYY-MM-DD" as UTC). For a wall clock in a given zone
 * use `parseZonedDateTime` / `fromZonedDateTimeInput` instead.
 */
type DateInput = Date | string | number;
const asDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

/** `date`'s wall clock in `timeZone`: year, 1-based month, day, hour, … */
export function wallClockOf(date: DateInput, timeZone: string): WallClock {
    return toLocal(asDate(date), timeZone);
}

/**
 * The instant a wall clock means in `timeZone` (time fields default to 0). A time in the
 * spring-forward gap resolves an hour later; a repeated fall-back time to its first instance.
 */
export function instantOf(
    wall: Pick<WallClock, "year" | "month" | "day"> & Partial<WallClock>,
    timeZone: string,
): Date {
    return fromLocal(
        {
            year: wall.year,
            month: wall.month,
            day: wall.day,
            hour: wall.hour ?? 0,
            minute: wall.minute ?? 0,
            second: wall.second ?? 0,
            millisecond: wall.millisecond ?? 0,
        },
        timeZone,
    );
}

/** The zone's UTC offset in minutes at `date` (-240 for EDT, 60 for BST). */
export function zoneOffsetMinutes(date: DateInput, timeZone: string): number {
    return offsetAt(asDate(date).getTime(), timeZone);
}

/** This runtime's IANA zone (what `dayjs.tz.guess()` returned). */
export function guessTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** True if `timeZone` is an IANA zone this runtime knows. */
export function isValidTimeZone(timeZone: string): boolean {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return true;
    } catch {
        return false;
    }
}

// ---- day, week and month boundaries in a zone --------------------------------

export type ZonedUnit = "day" | "week" | "month";

/** 0 = Sunday … 6 = Saturday, for a civil date. */
function weekdayOf(local: LocalDateTime): number {
    return civil(local.year, local.month, local.day).getUTCDay();
}

/**
 * The first instant of the day, week (starting `weekStartsOn`, Sunday by default) or month
 * that holds `date` in `timeZone`: local midnight, or the first instant after it when the
 * zone skips midnight.
 */
export function zonedStartOf(
    date: DateInput,
    unit: ZonedUnit,
    timeZone: string,
    weekStartsOn = 0,
): Date {
    const l = toLocal(asDate(date), timeZone);
    const midnight: LocalDateTime = { ...l, hour: 0, minute: 0, second: 0, millisecond: 0 };
    if (unit === "day") return fromLocal(midnight, timeZone);
    if (unit === "month") return fromLocal({ ...midnight, day: 1 }, timeZone);
    const back = (weekdayOf(l) - weekStartsOn + 7) % 7;
    return fromLocal(addLocalDaysTo(midnight, -back), timeZone);
}

/** The last millisecond of the day, week or month that holds `date` in `timeZone`. */
export function zonedEndOf(
    date: DateInput,
    unit: ZonedUnit,
    timeZone: string,
    weekStartsOn = 0,
): Date {
    const start = toLocal(zonedStartOf(date, unit, timeZone, weekStartsOn), timeZone);
    const day0: LocalDateTime = { ...start, hour: 0, minute: 0, second: 0, millisecond: 0 };
    const next =
        unit === "day"
            ? addLocalDaysTo(day0, 1)
            : unit === "week"
              ? addLocalDaysTo(day0, 7)
              : addLocalMonthsTo({ ...day0, day: 1 }, 1);
    return new Date(fromLocal(next, timeZone).getTime() - 1);
}

/** "YYYY-MM-DD": the calendar date of `date` in `timeZone` (a day key). */
export function zonedDayKey(date: DateInput, timeZone: string): string {
    return toZonedDateInput(asDate(date), timeZone);
}

/** Whether two instants fall on the same calendar date in `timeZone`. */
export function isSameZonedDay(a: DateInput, b: DateInput, timeZone: string): boolean {
    return zonedDayKey(a, timeZone) === zonedDayKey(b, timeZone);
}

/**
 * Whole calendar days from `from`'s date to `to`'s date in `timeZone` (1 for tomorrow, -1
 * for yesterday), whatever the times of day and however long the days in between.
 */
export function zonedDayDiff(from: DateInput, to: DateInput, timeZone: string): number {
    const a = toLocal(asDate(from), timeZone);
    const b = toLocal(asDate(to), timeZone);
    const ms = civil(b.year, b.month, b.day).getTime() - civil(a.year, a.month, a.day).getTime();
    return Math.round(ms / 86_400_000);
}

// ---- dayjs bridge and formatting ---------------------------------------------

/**
 * `date`'s wall clock in `timeZone` as a dayjs in UTC mode: its fields (`hour()`, `day()`,
 * `format()`) are the zone's wall clock, and `add` / `startOf` on it are plain calendar
 * steps that no DST change can skew. It is NOT the instant: read it back with
 * `fromWallDayjs`.
 *
 * A wall clock can't tell the two passes of the fall-back hour apart, so the round trip
 * `fromWallDayjs(wallDayjs(t, zone), zone)` moves an instant in the SECOND pass (01:30 EST
 * on the first Sunday of November) back an hour, to the first (01:30 EDT). Use it to
 * compute a new instant, never to carry a stored one through unchanged.
 */
export function wallDayjs(date: DateInput, timeZone: string): Dayjs {
    const l = toLocal(asDate(date), timeZone);
    return dayjs.utc(
        Date.UTC(l.year, l.month - 1, l.day, l.hour, l.minute, l.second, l.millisecond),
    );
}

/** The instant meant by a dayjs's own fields (as `format()` shows them), read in `timeZone`. */
export function fromWallDayjs(wall: Dayjs, timeZone: string): Date {
    return fromLocal(
        {
            year: wall.year(),
            month: wall.month() + 1,
            day: wall.date(),
            hour: wall.hour(),
            minute: wall.minute(),
            second: wall.second(),
            millisecond: wall.millisecond(),
        },
        timeZone,
    );
}

const offsetText = (minutes: number, colon: boolean) => {
    const sign = minutes < 0 ? "-" : "+";
    const abs = Math.abs(minutes);
    return `${sign}${pad(Math.floor(abs / 60))}${colon ? ":" : ""}${pad(abs % 60)}`;
};

/**
 * `date` formatted with a dayjs pattern as a person in `timeZone` reads it
 * ("MMM D, YYYY h:mm A"). `z` prints the zone's abbreviation ("EDT"), `Z` / `ZZ` its
 * offset ("-04:00" / "-0400"), `X` / `x` the instant's Unix seconds / milliseconds; text in
 * [brackets] is literal.
 */
export function formatZoned(date: DateInput, timeZone: string, pattern: string): string {
    const d = asDate(date);
    const zoned = pattern.replace(/\[[^\]]*]|ZZ|Z|z+|X|x/g, (token) => {
        if (token.startsWith("[")) return token;
        // Of the instant, not of the wall clock the pattern is applied to.
        if (token === "X") return `[${Math.floor(d.getTime() / 1000)}]`;
        if (token === "x") return `[${d.getTime()}]`;
        if (token === "ZZ") return `[${offsetText(offsetAt(d.getTime(), timeZone), false)}]`;
        if (token === "Z") return `[${offsetText(offsetAt(d.getTime(), timeZone), true)}]`;
        return `[${zoneAbbreviation(d, timeZone)}]`;
    });
    return wallDayjs(d, timeZone).format(zoned);
}
